import { pb } from '../pocketbase/Pocketbase'

// ============================================================================
// Types
// ============================================================================

export interface IBlogPost {
  id: string
  collectionId: string
  collectionName: string
  created: string
  updated: string
  title: string
  contentHtml: string
  slug: string
  coverImage: string | null
  author: string
}

export interface IBlogPostExpanded extends IBlogPost {
  expand?: {
    author?: {
      id: string
      username: string
      avatar?: string
    }
  }
}

export interface IBlogPostWithAuthor {
  id: string
  title: string
  contentHtml: string
  slug: string
  coverImageUrl: string | null
  authorId: string
  authorUsername: string
  authorAvatar: string | null
  created: string
  updated: string
}

export interface IBlogImage {
  id: string
  collectionId: string
  collectionName: string
  created: string
  updated: string
  file: string
  uploader: string
}

export interface IBlogImageWithUrl {
  id: string
  fileUrl: string
  uploaderId: string
  created: string
}

// ============================================================================
// Blog Posts API
// ============================================================================

/**
 * Get all blog posts (paginated)
 */
export async function getBlogPosts(
  page: number = 1,
  perPage: number = 10
): Promise<{ items: IBlogPostWithAuthor[]; totalItems: number; totalPages: number }> {
  const result = await pb.collection('blog_posts').getList<IBlogPostExpanded>(page, perPage, {
    sort: '-created',
    expand: 'author',
  })

  const items: IBlogPostWithAuthor[] = result.items.map((post) => {
    let coverImageUrl: string | null = null
    if (post.coverImage) {
      coverImageUrl = pb.files.getURL(post, post.coverImage)
    }

    let authorAvatar: string | null = null
    if (post.expand?.author?.avatar) {
      authorAvatar = pb.files.getURL(post.expand.author, post.expand.author.avatar)
    }

    return {
      id: post.id,
      title: post.title,
      contentHtml: post.contentHtml,
      slug: post.slug,
      coverImageUrl,
      authorId: post.author,
      authorUsername: post.expand?.author?.username || 'Unknown',
      authorAvatar,
      created: post.created,
      updated: post.updated,
    }
  })

  return {
    items,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

/**
 * Get a single blog post by slug
 */
export async function getBlogPostBySlug(slug: string): Promise<IBlogPostWithAuthor | null> {
  try {
    const post = await pb.collection('blog_posts').getFirstListItem<IBlogPostExpanded>(
      `slug = "${slug}"`,
      { expand: 'author' }
    )

    let coverImageUrl: string | null = null
    if (post.coverImage) {
      coverImageUrl = pb.files.getURL(post, post.coverImage)
    }

    let authorAvatar: string | null = null
    if (post.expand?.author?.avatar) {
      authorAvatar = pb.files.getURL(post.expand.author, post.expand.author.avatar)
    }

    return {
      id: post.id,
      title: post.title,
      contentHtml: post.contentHtml,
      slug: post.slug,
      coverImageUrl,
      authorId: post.author,
      authorUsername: post.expand?.author?.username || 'Unknown',
      authorAvatar,
      created: post.created,
      updated: post.updated,
    }
  } catch {
    return null
  }
}

/**
 * Get a single blog post by ID
 */
export async function getBlogPostById(id: string): Promise<IBlogPostWithAuthor | null> {
  try {
    const post = await pb.collection('blog_posts').getOne<IBlogPostExpanded>(id, {
      expand: 'author',
    })

    let coverImageUrl: string | null = null
    if (post.coverImage) {
      coverImageUrl = pb.files.getURL(post, post.coverImage)
    }

    let authorAvatar: string | null = null
    if (post.expand?.author?.avatar) {
      authorAvatar = pb.files.getURL(post.expand.author, post.expand.author.avatar)
    }

    return {
      id: post.id,
      title: post.title,
      contentHtml: post.contentHtml,
      slug: post.slug,
      coverImageUrl,
      authorId: post.author,
      authorUsername: post.expand?.author?.username || 'Unknown',
      authorAvatar,
      created: post.created,
      updated: post.updated,
    }
  } catch {
    return null
  }
}

/**
 * Create a new blog post (admin only)
 */
export async function createBlogPost(data: {
  title: string
  contentHtml: string
  slug: string
  coverImage?: File | null
}): Promise<IBlogPost> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('Must be logged in to create a blog post')
  }

  const formData = new FormData()
  formData.append('title', data.title)
  formData.append('contentHtml', data.contentHtml)
  formData.append('slug', data.slug)

  if (data.coverImage) {
    formData.append('coverImage', data.coverImage)
  }

  return await pb.collection('blog_posts').create<IBlogPost>(formData)
}

/**
 * Update an existing blog post (admin only)
 */
export async function updateBlogPost(
  id: string,
  data: {
    title?: string
    contentHtml?: string
    slug?: string
    coverImage?: File | null
    removeCoverImage?: boolean
  }
): Promise<IBlogPost> {
  const formData = new FormData()

  if (data.title !== undefined) {
    formData.append('title', data.title)
  }
  if (data.contentHtml !== undefined) {
    formData.append('contentHtml', data.contentHtml)
  }
  if (data.slug !== undefined) {
    formData.append('slug', data.slug)
  }
  if (data.coverImage) {
    formData.append('coverImage', data.coverImage)
  }
  if (data.removeCoverImage) {
    formData.append('coverImage', '')
  }

  return await pb.collection('blog_posts').update<IBlogPost>(id, formData)
}

/**
 * Delete a blog post (admin only)
 */
export async function deleteBlogPost(id: string): Promise<boolean> {
  return await pb.collection('blog_posts').delete(id)
}

// ============================================================================
// Blog Images API
// ============================================================================

/**
 * Upload a blog image
 */
export async function uploadBlogImage(file: File): Promise<IBlogImageWithUrl> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('Must be logged in to upload an image')
  }

  const formData = new FormData()
  formData.append('file', file)

  const image = await pb.collection('blog_images').create<IBlogImage>(formData)

  return {
    id: image.id,
    fileUrl: pb.files.getURL(image, image.file),
    uploaderId: image.uploader,
    created: image.created,
  }
}

/**
 * Get all blog images for the current user (paginated)
 */
export async function getBlogImages(
  page: number = 1,
  perPage: number = 20
): Promise<{ items: IBlogImageWithUrl[]; totalItems: number; totalPages: number }> {
  const result = await pb.collection('blog_images').getList<IBlogImage>(page, perPage, {
    sort: '-created',
  })

  const items: IBlogImageWithUrl[] = result.items.map((image) => ({
    id: image.id,
    fileUrl: pb.files.getURL(image, image.file),
    uploaderId: image.uploader,
    created: image.created,
  }))

  return {
    items,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

/**
 * Delete a blog image
 */
export async function deleteBlogImage(id: string): Promise<boolean> {
  return await pb.collection('blog_images').delete(id)
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a slug is available
 */
export async function isSlugAvailable(slug: string, excludeId?: string): Promise<boolean> {
  try {
    const filter = excludeId
      ? `slug = "${slug}" && id != "${excludeId}"`
      : `slug = "${slug}"`
    
    const result = await pb.collection('blog_posts').getList(1, 1, { filter })
    return result.totalItems === 0
  } catch {
    return true
  }
}
